import { makeAutoObservable } from 'mobx';
import {
	IEntityAPI,
	IEntityColumn,
	IGenericEntity,
	ITaskRequestFilterField,
	IUserConfigPackage,
	TaskRequestFilterFieldComparator,
} from '../API';
import { FilterOptionsState } from './FilterOptionsState';

export class FiltersState {
	private getApi: () => Promise<IEntityAPI<IGenericEntity>>;
	private parentEntityId: string = '';

	private userConfig: IUserConfigPackage;
	public show: boolean = false;
	public loading: boolean = true;
	public filterFields: FilterOptionsState[] = [];

	public selectedFilters: ITaskRequestFilterField[] = [];
	public permaFilters: ITaskRequestFilterField[] = [];

	private filtersChanged: (selectedFilters: ITaskRequestFilterField[]) => void;

	constructor(
		userConfig: IUserConfigPackage,
		getApi: () => Promise<IEntityAPI<IGenericEntity>>,
		filtersChanged: (selectedFilters: ITaskRequestFilterField[]) => void,
	) {
		this.getApi = getApi;
		this.userConfig = userConfig;
		this.filtersChanged = filtersChanged;
		makeAutoObservable(this);
	}

	public toggleFilterPanel(val: boolean): void {
		this.show = val;
	}

	public setParentEntityId(parentEntityId: string) {
		this.parentEntityId = parentEntityId;
	}

	public setFilterFields(filterFields: IEntityColumn[]): void {
		this.filterFields = [];
		for (var customField of filterFields) {
			var filterOption = new FilterOptionsState(
				customField,
				this.parentEntityId,
				this.getApi,
				this.userConfig,
			);
			this.filterFields.push(filterOption);
		}
	}

	public addFilter(
		fieldName: string,
		fieldValue: string,
		comparator: TaskRequestFilterFieldComparator,
	): void {
		let filterRequest = {
			fieldName: fieldName,
			fieldValue: fieldValue,
			comparator: comparator,
		} as ITaskRequestFilterField;
		this.selectedFilters.push(filterRequest);
	}

	public removeFilter(fieldName: string, fieldValue: string): void {
		let index = this.selectedFilters.findIndex(
			(filter) =>
				filter.fieldName === fieldName && filter.fieldValue === fieldValue,
		);
		this.selectedFilters.splice(index, 1);
	}

	public removeFiltersForField(fieldName: string) {
		this.selectedFilters = this.selectedFilters.filter(
			(f) => f.fieldName !== fieldName,
		);
	}

	public clearFilters() {
		this.selectedFilters = [];

		for (let filter of this.filterFields) {
			filter.selectedFilterOptions = [];
		}

		this.applyFilters();
	}

	public setPermaFilters(filters: ITaskRequestFilterField[]) {
		this.permaFilters = filters;
		this.updateFilters();
	}

	public setFilters(filters: ITaskRequestFilterField[]) {
		this.selectedFilters = filters;
		this.updateFilters();
	}

	private updateFilters(): void {
		this.filterFields.forEach((filterField) => {
			filterField.clearOptions();
		});

		this.selectedFilters.concat(this.permaFilters).forEach((filter) => {
			//Find the relevant filter options state and add this filter option
			var filterField = this.filterFields.find(
				(f) => f.columnInfo.id === filter.fieldName,
			);

			if (filterField) {
				filterField.addFilterOption({
					internal_value: filter.fieldValue,
					display_value: filter.fieldValue,
				});
			}
		});
	}

	public getAllFilters() {
		//Get all the selected filters, excluding any that appear in the permafilters.
		let selectedExceptPerma = (this.selectedFilters || []).filter((s) => {
			let alreadyInPerma = this.permaFilters.find(
				(p) => p.fieldName === s.fieldName,
			);
			return !alreadyInPerma;
		});

		return selectedExceptPerma.concat(this.permaFilters);
	}

	public applyFilters() {
		this.filtersChanged(this.getAllFilters());
	}
}
